import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Dna, 
  BarChart3, 
  TrendingUp, 
  Activity, 
  Search,
  Database,
  Cpu,
  AlertTriangle,
  CheckCircle,
  Zap,
  Target,
  Microscope,
  FileSearch,
  Brain
} from 'lucide-react';

interface GenAxisDemoProps {
  isActive: boolean;
}

const GenAxisDemo: React.FC<GenAxisDemoProps> = ({ isActive }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [sequenceData, setSequenceData] = useState<string[]>([]);
  const [discoveredMutations, setDiscoveredMutations] = useState<any[]>([]);

  const analysisSteps = [
    {
      name: 'upload',
      icon: Database,
      title: 'Sample Upload',
      description: 'Processing genomic data files',
      color: 'text-blue-500',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      borderColor: 'border-blue-200 dark:border-blue-700'
    },
    {
      name: 'sequence',
      icon: Search,
      title: 'Sequence Analysis',
      description: 'Analyzing DNA sequences',
      color: 'text-purple-500',
      bgColor: 'bg-purple-50 dark:bg-purple-900/20',
      borderColor: 'border-purple-200 dark:border-purple-700'
    },
    {
      name: 'compute',
      icon: Cpu,
      title: 'ML Processing',
      description: 'Running machine learning algorithms',
      color: 'text-orange-500',
      bgColor: 'bg-orange-50 dark:bg-orange-900/20',
      borderColor: 'border-orange-200 dark:border-orange-700'
    },
    {
      name: 'analysis',
      icon: Brain,
      title: 'Pattern Recognition',
      description: 'Identifying genetic patterns',
      color: 'text-indigo-500',
      bgColor: 'bg-indigo-50 dark:bg-indigo-900/20',
      borderColor: 'border-indigo-200 dark:border-indigo-700'
    },
    {
      name: 'results',
      icon: Target,
      title: 'Results Ready',
      description: 'Analysis complete',
      color: 'text-green-500',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      borderColor: 'border-green-200 dark:border-green-700'
    }
  ];

  const sampleSequences = [
    'ATCGATCGATCG',
    'GCTAGCTAGCTA',
    'TTAACCGGTTAA',
    'CGATCGATCGAT',
    'GGCCTTAACCGG'
  ];

  const mutations = [
    {
      id: 1,
      position: 'Chr7:140453136',
      type: 'SNV',
      gene: 'BRAF',
      impact: 'High',
      significance: 'Pathogenic',
      frequency: '0.23%'
    },
    {
      id: 2,
      position: 'Chr17:7577558',
      type: 'Deletion',
      gene: 'TP53',
      impact: 'High',
      significance: 'Likely Pathogenic',
      frequency: '0.05%'
    },
    {
      id: 3,
      position: 'Chr13:32906729',
      type: 'Insertion',
      gene: 'BRCA2',
      impact: 'Moderate',
      significance: 'VUS',
      frequency: '0.12%'
    }
  ];

  useEffect(() => {
    if (isActive) {
      let currentStepIndex = 0;
      let progress = 0;
      
      const stepInterval = setInterval(() => {
        if (currentStepIndex < analysisSteps.length - 1) {
          currentStepIndex++;
          setCurrentStep(currentStepIndex);
          progress = 0;
          setAnalysisProgress(0);
        } else {
          currentStepIndex = 0;
          setCurrentStep(0);
          progress = 0;
          setAnalysisProgress(0);
          setSequenceData([]);
          setDiscoveredMutations([]);
        }
      }, 3000);

      const progressInterval = setInterval(() => {
        if (progress < 100) {
          progress += 2;
          setAnalysisProgress(progress);
        }
      }, 60);

      return () => {
        clearInterval(stepInterval);
        clearInterval(progressInterval);
      };
    }
  }, [isActive]);

  // Generate sequence data animation
  useEffect(() => {
    if (currentStep === 1 && isActive) {
      let index = 0;
      const sequenceInterval = setInterval(() => {
        if (index < sampleSequences.length) {
          setSequenceData(prev => [...prev, sampleSequences[index]]);
          index++;
        }
      }, 500);

      return () => clearInterval(sequenceInterval);
    }
  }, [currentStep, isActive]);

  // Generate mutations discovery
  useEffect(() => {
    if (currentStep === 4 && isActive) {
      setDiscoveredMutations([]); // Clear first
      let index = 0;
      const mutationInterval = setInterval(() => {
        if (index < mutations.length) {
          setDiscoveredMutations(prev => [...prev, mutations[index]]);
          index++;
        }
      }, 600);

      return () => clearInterval(mutationInterval);
    } else if (currentStep !== 4) {
      setDiscoveredMutations([]);
    }
  }, [currentStep, isActive]);

  const renderUploadStep = () => (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Sample Files</span>
        <span className="text-xs text-slate-500">3 files detected</span>
      </div>
      
      {['sample_001.vcf', 'sample_002.bam', 'reference.fasta'].map((file, index) => (
        <motion.div
          key={file}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.2 }}
          className="flex items-center justify-between p-2 bg-white dark:bg-slate-700 rounded border border-slate-200 dark:border-slate-600"
        >
          <div className="flex items-center gap-2">
            <Database className="w-3 h-3 text-blue-500" />
            <span className="text-xs text-slate-600 dark:text-slate-400">{file}</span>
          </div>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.5 + index * 0.2 }}
          >
            <CheckCircle className="w-3 h-3 text-green-500" />
          </motion.div>
        </motion.div>
      ))}
    </div>
  );

  const renderSequenceStep = () => (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-3">
        <Microscope className="w-4 h-4 text-purple-500" />
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">DNA Sequences</span>
      </div>
      
      <div className="space-y-2 max-h-32 overflow-hidden">
        {sequenceData.map((sequence, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-2 bg-white dark:bg-slate-700 rounded border border-slate-200 dark:border-slate-600"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono text-slate-600 dark:text-slate-400">
                {sequence}...
              </span>
              <div className="flex items-center gap-1">
                <Activity className="w-3 h-3 text-purple-400" />
                <span className="text-xs text-purple-500">Analyzing</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );

  const renderComputeStep = () => (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-3">
        <Cpu className="w-4 h-4 text-orange-500" />
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">ML Processing</span>
      </div>
      
      <div className="grid grid-cols-2 gap-2">
        {['Neural Network', 'Random Forest', 'SVM Analysis', 'Deep Learning'].map((algorithm, index) => (
          <motion.div
            key={algorithm}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.15 }}
            className="p-2 bg-white dark:bg-slate-700 rounded border border-slate-200 dark:border-slate-600"
          >
            <div className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
              {algorithm}
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-slate-200 dark:bg-slate-600 rounded-full h-1">
                <motion.div
                  className="bg-orange-500 h-1 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(analysisProgress + index * 10, 100)}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
              <span className="text-xs text-slate-500">
                {Math.min(analysisProgress + index * 10, 100)}%
              </span>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );

  const renderAnalysisStep = () => (
    <div className="space-y-3">
      <div className="flex items-center gap-2 mb-3">
        <Brain className="w-4 h-4 text-indigo-500" />
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Pattern Recognition</span>
      </div>
      
      <div className="space-y-2">
        <div className="p-2 bg-white dark:bg-slate-700 rounded border border-slate-200 dark:border-slate-600">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-slate-700 dark:text-slate-300">Genetic Variants</span>
            <span className="text-xs text-indigo-500">Scanning...</span>
          </div>
          <div className="w-full bg-slate-200 dark:bg-slate-600 rounded-full h-1">
            <motion.div
              className="bg-indigo-500 h-1 rounded-full"
              animate={{ width: [`0%`, `100%`, `0%`] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>
        </div>
        
        <div className="p-2 bg-white dark:bg-slate-700 rounded border border-slate-200 dark:border-slate-600">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-slate-700 dark:text-slate-300">Mutation Impact</span>
            <span className="text-xs text-indigo-500">Evaluating...</span>
          </div>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((bar) => (
              <motion.div
                key={bar}
                className="flex-1 bg-slate-200 dark:bg-slate-600 rounded h-4"
                animate={{ 
                  backgroundColor: analysisProgress > bar * 20 ? '#6366f1' : undefined 
                }}
                transition={{ delay: bar * 0.1 }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderResultsStep = () => (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-green-500" />
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Mutations Found</span>
        </div>
        <span className="text-xs text-green-500 font-medium">{discoveredMutations.length} variants</span>
      </div>
      
      <div className="space-y-2 max-h-40 overflow-hidden">
        {discoveredMutations.filter(mutation => mutation && mutation.gene).map((mutation, index) => (
          <motion.div
            key={mutation.id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.1 }}
            className="p-2 bg-white dark:bg-slate-700 rounded border border-slate-200 dark:border-slate-600"
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                {mutation.gene}
              </span>
              <div className="flex items-center gap-1">
                {mutation.impact === 'High' ? (
                  <AlertTriangle className="w-3 h-3 text-red-400" />
                ) : (
                  <CheckCircle className="w-3 h-3 text-yellow-400" />
                )}
                <span className={`text-xs ${mutation.impact === 'High' ? 'text-red-400' : 'text-yellow-400'}`}>
                  {mutation.impact}
                </span>
              </div>
            </div>
            <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400">
              <span>{mutation.position}</span>
              <span>{mutation.type}</span>
              <span>{mutation.frequency}</span>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0: return renderUploadStep();
      case 1: return renderSequenceStep();
      case 2: return renderComputeStep();
      case 3: return renderAnalysisStep();
      case 4: return renderResultsStep();
      default: return renderUploadStep();
    }
  };

  const currentStepData = analysisSteps[currentStep];

  return (
    <AnimatePresence>
      {isActive && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="w-full h-80 bg-gradient-to-br from-slate-50 to-white dark:from-slate-800 dark:to-slate-700 rounded-xl p-6 shadow-xl border border-slate-200 dark:border-slate-600"
        >
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Dna className="w-6 h-6 text-primary-600" />
                <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200">GenAxis</h3>
              </div>
              <div className="flex items-center gap-2">
                <motion.div
                  animate={{ rotate: currentStep < 4 ? 360 : 0 }}
                  transition={{ duration: 2, repeat: currentStep < 4 ? Infinity : 0, ease: "linear" }}
                >
                  <Zap className="w-4 h-4 text-accent-500" />
                </motion.div>
                <span className="text-xs text-slate-500">
                  {currentStep < 4 ? 'Processing' : 'Complete'}
                </span>
              </div>
            </div>

            {/* Step Indicator */}
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`p-3 rounded-lg mb-4 ${currentStepData.bgColor} border ${currentStepData.borderColor}`}
            >
              <div className="flex items-center gap-3">
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  {React.createElement(currentStepData.icon, {
                    className: `w-5 h-5 ${currentStepData.color}`
                  })}
                </motion.div>
                <div>
                  <div className={`font-semibold text-sm ${currentStepData.color}`}>
                    {currentStepData.title}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    {currentStepData.description}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Main Content */}
            <div className="flex-1 overflow-hidden">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {renderCurrentStep()}
              </motion.div>
            </div>

            {/* Progress Bar */}
            <div className="mt-4">
              <div className="flex justify-between text-xs text-slate-500 mb-1">
                <span>Step {currentStep + 1} of {analysisSteps.length}</span>
                <span>{Math.round(((currentStep + 1) / analysisSteps.length) * 100)}% Complete</span>
              </div>
              <div className="w-full bg-slate-200 dark:bg-slate-600 rounded-full h-2">
                <motion.div
                  className="bg-gradient-to-r from-primary-500 via-accent-500 to-green-500 h-2 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${((currentStep + 1) / analysisSteps.length) * 100}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GenAxisDemo;